import {
  FileText,
  Box,
  Shield,
  Link2,
  Database,
  Layers,
  ArrowRight,
  X,
} from "lucide-react";
import { useVisualizerStore } from "./store";

const iconMap: Record<string, React.ElementType> = {
  root: Layers,
  metadata: FileText,
  imports: Link2,
  import: Link2,
  components: Box,
  component: Box,
  capabilities: Shield,
  capability: Shield,
  resources: Database,
  resource: Database,
};

const typeColors: Record<string, string> = {
  service: "bg-blue-100 text-blue-800",
  software: "bg-emerald-100 text-emerald-800",
  policy: "bg-amber-100 text-amber-800",
  process: "bg-purple-100 text-purple-800",
  hardware: "bg-slate-200 text-slate-800",
};

export function ComponentDetailsPanel() {
  const { selectedNode, clearSelection, selectNode } = useVisualizerStore();

  if (!selectedNode) {
    return (
      <div className="flex flex-col items-center justify-center h-full dark:text-white/60 text-[#94a3b8] p-6">
        <FileText className="size-10 mb-3 opacity-40" />
        <p className="text-sm font-medium">No selection</p>
        <p className="text-xs mt-1">Click a node to view details</p>
      </div>
    );
  }

  const Icon = iconMap[selectedNode.type] || Layers;
  const data = selectedNode.data;
  const rels = selectedNode.relationships || [];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-[#e5e7eb]">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-3">
            <div className="size-9 rounded-lg dark:bg-white/[0.05] bg-[#f1f5f9] flex items-center justify-center flex-shrink-0">
              <Icon className="size-4 text-[#475569]" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold dark:text-white text-[#0f172a] leading-tight">
                {selectedNode.label}
              </h3>
              <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold tracking-wide ${typeColors[String(data.type)] || "dark:bg-white/[0.05] bg-[#f1f5f9] text-[#64748b]"}`}>
                {selectedNode.type}
              </span>
            </div>
          </div>
          <button
            onClick={clearSelection}
            className="p-1 rounded dark:text-white/60 text-[#64748b] hover:bg-[#f1f5f9] dark:hover:bg-white/[0.1] hover:dark:text-white hover:text-[#0f172a] transition-colors"
          >
            <X className="size-3.5" />
          </button>
        </div>
      </div>

      {/* Properties */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Key-Value pairs */}
        <div className="space-y-1">
          {Object.entries(data).map(([key, value]) => {
            if (value === null || value === undefined) return null;
            if (typeof value === "object") return null; // Skip complex objects inline
            return (
              <div
                key={key}
                className="flex items-start gap-3 py-1.5 border-b border-[#f1f5f9] last:border-0"
              >
                <span className="text-[10px] font-bold dark:text-white/60 text-[#94a3b8] tracking-wide w-28 flex-shrink-0 pt-0.5">
                  {key.replace(/([A-Z])/g, " $1").replace(/-/g, " ")}
                </span>
                <span className="text-xs dark:text-white text-[#0f172a] break-all">
                  {typeof value === "number" ? String(value) : String(value)}
                </span>
              </div>
            );
          })}
        </div>

        {/* Description (if long) */}
        {data.description && typeof data.description === "string" && data.description.length > 50 && (
          <div className="space-y-1">
            <span className="text-[10px] font-bold dark:text-white/60 text-[#94a3b8] tracking-wide">
              Description
            </span>
            <p className="text-xs dark:text-white/70 text-[#374151] leading-relaxed dark:bg-white/[0.02] bg-[#f8fafc] rounded-lg p-3 border border-[#e5e7eb]">
              {data.description}
            </p>
          </div>
        )}

        {/* Complex objects */}
        {Object.entries(data).map(([key, value]) => {
          if (!value || typeof value !== "object") return null;
          if (key === "description") return null;
          return (
            <div key={key} className="space-y-1">
              <span className="text-[10px] font-bold dark:text-white/60 text-[#94a3b8] tracking-wide">
                {key.replace(/([A-Z])/g, " $1").replace(/-/g, " ")}
              </span>
              <pre className="text-[10px] dark:text-white/70 text-[#374151] leading-relaxed dark:bg-white/[0.02] bg-[#f8fafc] rounded-lg p-3 border border-[#e5e7eb] overflow-x-auto max-h-40">
                {JSON.stringify(value, null, 2)}
              </pre>
            </div>
          );
        })}

        {/* Relationships */}
        {rels.length > 0 && (
          <div className="space-y-2">
            <span className="text-[10px] font-bold dark:text-white/60 text-[#94a3b8] tracking-wide">
              Relationships
            </span>
            {rels.map((rel, idx) => (
              <button
                key={idx}
                onClick={() => selectNode(rel.targetId)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-[#e5e7eb] hover:dark:bg-white/[0.05] bg-[#f1f5f9] transition-colors text-left group"
              >
                <ArrowRight className="size-3 dark:text-white/60 text-[#94a3b8] group-hover:dark:text-white text-[#0f172a] transition-colors flex-shrink-0" />
                <div className="min-w-0">
                  <span className="text-[10px] font-bold dark:text-white/60 text-[#94a3b8] ">
                    {rel.label}
                  </span>
                  <p className="text-xs font-medium dark:text-white text-[#0f172a] truncate">
                    {rel.targetLabel}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
